import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, TrendingUp, TrendingDown, Minus, Target, Package, BarChart3 } from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrendsPage() {
  const { user, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [period, setPeriod] = useState('weekly');
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchTrends();
    }
  }, [selectedLocation, period]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
      
      if (isManager && user?.location_id) {
        setSelectedLocation(user.location_id);
      } else if (res.data.length > 0) {
        setSelectedLocation(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const res = await axios.get(`${API}/trends/${selectedLocation}`, {
        params: { period }
      });
      setTrends(res.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  };

  const getTrendIcon = (current, previous) => {
    if (!previous || previous === 0) return <Minus className="w-4 h-4 text-slate-400" />;
    const change = ((current - previous) / previous) * 100;
    if (change > 5) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (change < -5) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getStatusColor = (value, target) => {
    const ratio = value / target;
    if (ratio >= 1) return 'text-emerald-600';
    if (ratio >= 0.8) return 'text-amber-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="trends-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Product Mix Trends
          </h1>
          <p className="text-slate-600 mt-1">Track performance trends over time</p>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              {!isManager && (
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="w-64" data-testid="trends-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Tabs value={period} onValueChange={setPeriod}>
                <TabsList>
                  <TabsTrigger value="weekly" data-testid="trends-tab-weekly">Weekly</TabsTrigger>
                  <TabsTrigger value="monthly" data-testid="trends-tab-monthly">Monthly</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardContent>
        </Card>

        {/* Targets Reference */}
        {trends && (
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-violet-600" />
                  <span className="text-slate-500">Targets:</span>
                </div>
                <div>
                  <span className="text-slate-600">Supplemental Sales:</span>
                  <span className="font-mono font-semibold ml-1">{trends.targets.supplemental_pct}%</span>
                </div>
                <div>
                  <span className="text-slate-600">Digital Mix:</span>
                  <span className="font-mono font-semibold ml-1">{trends.targets.digital_mix_pct}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trend Data */}
        {trends && trends.data.length > 0 ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(() => {
                const latest = trends.data[trends.data.length - 1];
                const previous = trends.data.length > 1 ? trends.data[trends.data.length - 2] : null;
                
                return (
                  <>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Latest Score</p>
                            <p className="text-2xl font-bold font-mono">{latest.average_score}</p>
                          </div>
                          {getTrendIcon(latest.average_score, previous?.average_score)}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Supplemental %</p>
                            <p className={cn(
                              "text-2xl font-bold font-mono",
                              getStatusColor(latest.supplemental_pct, trends.targets.supplemental_pct)
                            )}>
                              {latest.supplemental_pct}%
                            </p>
                          </div>
                          {getTrendIcon(latest.supplemental_pct, previous?.supplemental_pct)}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Digital Mix %</p>
                            <p className={cn(
                              "text-2xl font-bold font-mono",
                              getStatusColor(latest.digital_mix_pct, trends.targets.digital_mix_pct)
                            )}>
                              {latest.digital_mix_pct}%
                            </p>
                          </div>
                          {getTrendIcon(latest.digital_mix_pct, previous?.digital_mix_pct)}
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Supp $/Hour</p>
                            <p className="text-2xl font-bold font-mono">
                              ${latest.supplemental_per_labor_hour}
                            </p>
                          </div>
                          {getTrendIcon(latest.supplemental_per_labor_hour, previous?.supplemental_per_labor_hour)}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Detailed Trend Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                  {period === 'weekly' ? 'Weekly' : 'Monthly'} Trend Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Period</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Total Sales</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Supplemental</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Supp %</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Digital %</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Supp/Hour</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Score</th>
                        <th className="text-center py-3 px-4 font-semibold text-slate-600">Entries</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.data.map((row, index) => {
                        const prevRow = index > 0 ? trends.data[index - 1] : null;
                        return (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{row.period_label}</td>
                            <td className="py-3 px-4 text-right font-mono">{formatCurrency(row.total_sales)}</td>
                            <td className="py-3 px-4 text-right font-mono">{formatCurrency(row.supplemental_sales)}</td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span className={cn(
                                  "font-mono font-semibold",
                                  getStatusColor(row.supplemental_pct, trends.targets.supplemental_pct)
                                )}>
                                  {row.supplemental_pct}%
                                </span>
                                {getTrendIcon(row.supplemental_pct, prevRow?.supplemental_pct)}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span className={cn(
                                  "font-mono",
                                  getStatusColor(row.digital_mix_pct, trends.targets.digital_mix_pct)
                                )}>
                                  {row.digital_mix_pct}%
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right font-mono">${row.supplemental_per_labor_hour}</td>
                            <td className="py-3 px-4 text-right">
                              <Badge 
                                className={cn(
                                  "font-mono",
                                  row.average_score >= 85 ? 'bg-emerald-500' :
                                  row.average_score >= 70 ? 'bg-lime-500' :
                                  row.average_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                                )}
                              >
                                {row.average_score}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center text-slate-500">{row.entry_count}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No trend data available</h3>
              <p className="text-slate-500">Submit daily entries to start tracking trends</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
